import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmDialog, Drawer, IconButton } from "@outpave/ds";
import { Badge, Button, useMediaQuery } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { BubbleChatIcon } from "@hugeicons-pro/core-stroke-rounded";
import { useMutation, useQuery } from "@apollo/client";
import {
  GET_TRANSACTION,
  UPDATE_TRANSACTION,
} from "../../../graphql/transaction";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import UserProfileDrawer from "components/common/users/UserProfileDrawer";
import { getGraphqlErrorMessage } from "../../../utils/graphql";
import { ParentType } from "../../../enums/Comments";
import CommentsPanel from "components/common/comments/CommentsPanel";
import { useComments } from "hooks/useComments";
import IdNotFoundPage from "../../common/IdNotFoundPage";
import TransactionsType from "enums/TransactionsType";
import ReimbursementDetails from "./ReimbursementDetails";
import TransactionDetails from "./TransactionDetails";
import TransactionSections from "./TransactionSections";
import { useTransactionRequirements } from "hooks/useTransactionRequirements";
import { useAdminTools } from "hooks/useAdminTools";
import { Stack } from "components/layout/reusableLayouts";

// The DS drawer's own default width. Mirrored here only because the comments
// panel docks flush against the paper edge and has to know where it is.
const DRAWER_WIDTH = 420;

interface TransactionDetailsDrawerProps {
  open: boolean;
  transactionId: string;
  drawerTitle?: string;
  handleRefreshTransactions?: () => void;
  handleCloseDrawer: () => void;
  /** Fires once the drawer has slid out. */
  onAfterClose?: () => void;
}

const TransactionDetailsDrawer: React.FC<TransactionDetailsDrawerProps> = (
  props,
) => {
  const {
    open,
    handleCloseDrawer,
    transactionId,
    handleRefreshTransactions,
    drawerTitle = "Transaction detail",
    onAfterClose,
  } = props;

  const { t } = useTranslation();
  const adminTools = useAdminTools();

  // The panel docks beside the drawer only when there is room for both.
  const commentsDockable = useMediaQuery(`(min-width:${DRAWER_WIDTH + 520}px)`);
  const [commentsOpen, setCommentsOpen] = useState(false);

  const commentActions = useComments(transactionId, ParentType.TRANSACTION);
  const commentCount = commentActions.comments?.length ?? 0;

  const [openUserProfileDrawer, setUserProfileDrawer] =
    useState<boolean>(false);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [close, setClose] = useState<boolean>(false);
  const [showIntegrationGuessWarning, setShowIntegrationGuessWarning] =
    useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

  const [refreshLineItems, setRefreshLineItems] = useState<number>(0);

  const {
    refetch: refetchTransactionData,
    data: transactionData,
    loading: transactionDataIsLoading,
  } = useQuery(GET_TRANSACTION, {
    variables: {
      id: transactionId,
    },
    fetchPolicy: "network-only",
    skip: isEmpty(transactionId),
  });

  const transaction = transactionData?.transaction;
  const hasTransactionData = !!transaction?.id;
  const showContent = hasTransactionData;
  const showNotFoundOrLoading = !hasTransactionData;

  const isReimbursement =
    transaction?.type === TransactionsType.MILEAGE ||
    transaction?.type === TransactionsType.CASH;

  const [updateTransaction, { loading: updatingTransaction }] = useMutation(
    UPDATE_TRANSACTION,
    {
      onCompleted: () => {
        refetchTransactionData();
        setRefreshLineItems((prev) => prev + 1);
        toast.success(t("Transaction successfully updated"));
      },
      onError: (error) => {
        toast.error(getGraphqlErrorMessage(error?.graphQLErrors));
      },
    },
  );

  const { formik: formikTransactionRequirements } = useTransactionRequirements({
    transaction,
    transactionId,
    updateTransaction,
    handleRefreshTransactions,
  });

  const refreshAfterAction = () => {
    handleRefreshTransactions?.();
    refetchTransactionData();
  };

  const handleOpenUserProfileDrawer = (userId: string) => {
    setSelectedUserId(userId);
    setUserProfileDrawer(true);
  };

  const handleCloseOpenUserProfileDrawer = () => {
    setUserProfileDrawer(false);
    setSelectedUserId("");
  };

  const handleUpdateUser = (error?: string) => {
    if (error) {
      toast.error(error);
      return;
    }
    toast.success(t("User updated!"));
  };

  // Confirming an integration's category guess is accounting cleanup, so it only prompts managers and above.
  const integrationGuessPrompt = adminTools && showIntegrationGuessWarning;
  const hasUnsavedChanges =
    formikTransactionRequirements.dirty || integrationGuessPrompt;

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedChangesModal(true);
    } else {
      setClose(true);
      setShowIntegrationGuessWarning(false);
    }
  };

  useEffect(() => {
    if (integrationGuessPrompt) {
      toast.success(
        t(
          "This category was set prior to your integration We updated it for you",
        ),
      );
    }
  }, [integrationGuessPrompt, t]);

  useEffect(() => {
    if (close && !updatingTransaction) {
      handleCloseDrawer();
      setClose(false);
    }
  }, [close, handleCloseDrawer, updatingTransaction]);

  return (
    <>
      <Drawer
        open={open}
        onClose={handleClose}
        onAfterClose={() => {
          formikTransactionRequirements.resetForm();
          setShowIntegrationGuessWarning(false);
          setCommentsOpen(false);
          onAfterClose?.();
        }}
        title={t(drawerTitle)}
        loading={transactionDataIsLoading || updatingTransaction}
        disableEnforceFocus={commentsOpen}
        headerAction={
          <IconButton
            variant="ghost"
            aria-label={t("Comments")}
            onClick={() => setCommentsOpen((wasOpen) => !wasOpen)}
          >
            <Badge badgeContent={commentCount} color="primary">
              <HugeiconsIcon icon={BubbleChatIcon} size={18} />
            </Badge>
          </IconButton>
        }
        footer={
          <>
            <Button
              variant="default-soft"
              onClick={() => {
                handleClose();
              }}
              fullWidth
            >
              {t("Exit")}
            </Button>

            {hasUnsavedChanges && (
              <Button
                variant="primary-filled"
                type="submit"
                disabled={updatingTransaction}
                onClick={() => {
                  formikTransactionRequirements.submitForm();
                }}
                fullWidth
              >
                {updatingTransaction ? t("Saving") : t("Save")}
              </Button>
            )}
          </>
        }
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          {showNotFoundOrLoading && (
            <IdNotFoundPage
              title={"Transaction"}
              isLoading={transactionDataIsLoading}
              showSkeleton={true}
            />
          )}

          {showContent && (
            <Stack gap="var(--space-5)">
              {!isReimbursement && (
                <TransactionDetails
                  transaction={transaction}
                  adminTools={adminTools}
                  handleOpenUserProfileDrawer={handleOpenUserProfileDrawer}
                  handleRefresh={refreshAfterAction}
                />
              )}

              {isReimbursement && (
                <ReimbursementDetails
                  transaction={transaction}
                  adminTools={adminTools}
                  handleOpenUserProfileDrawer={handleOpenUserProfileDrawer}
                  handleRefresh={(refresh) => {
                    if (refresh) refreshAfterAction();
                  }}
                />
              )}

              <TransactionSections
                key={transactionId}
                transaction={transaction}
                formik={formikTransactionRequirements}
                refreshLineItems={refreshLineItems}
                setShowIntegrationGuessWarning={setShowIntegrationGuessWarning}
                refetchTransactionData={refetchTransactionData}
                handleRefreshTransactions={handleRefreshTransactions}
              />
            </Stack>
          )}
        </form>
      </Drawer>
      <CommentsPanel
        open={open && commentsOpen}
        onClose={() => setCommentsOpen(false)}
        drawerWidth={DRAWER_WIDTH}
        docked={commentsDockable}
        commentActions={commentActions}
        openProfileOnMention={adminTools}
      />

      {adminTools && (
        <UserProfileDrawer
          selectedUserId={selectedUserId}
          open={openUserProfileDrawer}
          handleCloseDrawer={handleCloseOpenUserProfileDrawer}
          handleUpdateUser={(error?: string) => {
            handleUpdateUser(error);
          }}
        />
      )}

      <ConfirmDialog
        open={showUnsavedChangesModal}
        title={t("Unsaved Changes")}
        message={t("You have unsaved changes, are you sure you want to leave?")}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        onConfirm={() => {
          setShowUnsavedChangesModal(false);
          setShowIntegrationGuessWarning(false);
          setClose(true);
        }}
        onCancel={() => setShowUnsavedChangesModal(false)}
      />
    </>
  );
};

export default TransactionDetailsDrawer;
